/**
 * THE ROSTER LOCK. A pure module, like scoring and seating, because four callers need the same
 * answer and none of them should be computing it: the repository enforces it, the director's roster
 * warns about it, the public site stops selling seats at it, and the print pipeline (Phase 7) is the
 * reason it exists at all.
 *
 * The rule, from BUILD-SPEC §116: a league's roster locks at a configurable cutoff — ten days
 * before the first session by default — so that HQ can print name tents and score sheets that match
 * who is actually playing. After the lock, roster edits are NOT blocked; they require a director's
 * explicit override and flag the print pack `stale` so HQ can decide whether to reprint.
 *
 * That distinction is the whole design. Blocking would be wrong: people drop out three days before a
 * league and somebody has to be moved in. What must not happen is a roster changing SILENTLY under a
 * pack that has already been printed, shipped, or is halfway through being assembled — the platform
 * flags, HQ decides, because the answer depends on how far along the physical pack is.
 */

/** Ten days before session one. BUILD-SPEC §116's default. */
export const DEFAULT_ROSTER_LOCK_DAYS = 10;

/**
 * How long before the lock the director starts being told about it.
 *
 * Two weeks, so the warning arrives BEFORE the lock rather than at it. A director who learns her
 * roster is locked on the day it locks has been told a fact, not given a chance — the point of the
 * banner is the four days of slack between "this is about to happen" and "this has happened".
 */
export const ROSTER_LOCK_WARNING_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Where the lock falls for a league whose first session is on this date.
 *
 * Deliberately NOT clamped to the present. A director creating a league that starts next week gets a
 * lock date in the past and a roster that is immediately locked, which is correct and is exactly the
 * thing she should be told: there is no time to print anything. Quietly moving the date to "today"
 * would hide a real scheduling problem behind a date that looks fine.
 */
export function defaultRosterLockAt(
  firstSessionAt: Date,
  days: number = DEFAULT_ROSTER_LOCK_DAYS,
): Date {
  return new Date(firstSessionAt.getTime() - days * DAY_MS);
}

export interface RosterLockState {
  /** Null when no lock date is set — an older league, or one whose sessions moved. */
  lockAt: Date | null;
  locked: boolean;
  /** Whole days until the lock; negative once past it, null when there is no lock date. */
  daysUntil: number | null;
  /** Inside the warning window and not yet locked — the banner's condition. */
  approaching: boolean;
}

/**
 * `now` is a REQUIRED argument rather than a call to `Date.now()` inside.
 *
 * This module has to be testable at an arbitrary instant, and a function that reads the clock itself
 * can only be tested by mocking the clock. Every caller has a `now` to hand.
 */
export function rosterLockState(lockAt: Date | null | undefined, now: Date): RosterLockState {
  if (!lockAt) {
    return { lockAt: null, locked: false, daysUntil: null, approaching: false };
  }

  const msUntil = lockAt.getTime() - now.getTime();
  const locked = msUntil <= 0;

  /**
   * Rounded UP for a future lock, so "1 day" means "some time tomorrow" rather than rounding 23
   * hours down to zero and reading as "today". A director acts on this number.
   */
  const daysUntil = Math.ceil(msUntil / DAY_MS);

  return {
    lockAt,
    locked,
    daysUntil,
    approaching: !locked && daysUntil <= ROSTER_LOCK_WARNING_DAYS,
  };
}
